import React, { useRef, useState } from 'react'
import { Attachment, Goal } from 'Generated/graphql-types'
import { useMutation } from '@apollo/client'
import dayjs from 'dayjs'
import { Menu, MenuItem } from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import DescriptionIcon from '@mui/icons-material/Description'
import PhotoIcon from '@mui/icons-material/Photo'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined'
import { useAppState } from '../../../../../Context/AppContext/AppContext'
import CREATE_ATTACHMENT from '../../../../../Apollo/Mutations/CreateAttachment'
import DELETE_ATTACHMENT from '../../../../../Apollo/Mutations/DeleteAttachment'
import { GOALS } from '../../../../../Apollo/Queries/Goals'
import { AttachmentUpdateDialog } from './AttachmentUpdateDialog'
import ConfirmActionDialogSong from '../../../../SongComponents/ConfirmActionDialogSong/ConfirmActionDialogSong'
import TitleWithTooltipSong from '../../../../SongComponents/TitleWithTooltipSong/TitleWithTooltipSong'
import CloudinaryCustomUploads from '../../../../../Utils/CloudinaryCustomUploads'
import {
  Container,
  HeaderRow,
  HeaderCell,
  ScrollableContent,
  AttachmentRow,
  FileNameCell,
  UserCell,
  DateCell,
  ActionsCell,
  AttachmentIcon,
  FileName,
  UserAvatar,
  UserInfo,
  ActionsButton,
  EmptyStateContainer,
  IconContainer,
  EmptyStateTitle,
  EmptyStateText,
} from './GoalAttachments.styled'
import {
  HeaderAndButtonContainer,
  ButtonContainer,
} from '../../GoalPage/Updates/UpdatesTab.styled'

type Props = {
  goal: Goal
}

export const GoalAttachments = ({ goal }: Props) => {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedAttachment, setSelectedAttachment] =
    useState<Attachment | null>(null)

  const {
    nonprofitOrgConfig: { orgId: nonprofitOrgId },
  } = useAppState()

  const [createAttachment] = useMutation(CREATE_ATTACHMENT, {
    refetchQueries: [
      {
        query: GOALS,
        variables: { activeOnly: true, nonprofitOrgId },
      },
    ],
  })
  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT, {
    refetchQueries: [
      {
        query: GOALS,
        variables: { activeOnly: true, nonprofitOrgId },
      },
    ],
  })
  const goalId = goal.id
  const [activeUpdate, setActiveUpdate] = useState<Attachment>()
  const [isUpdateOpen, setIsUpdateOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement | null>(null)

  const CLOUDINARY_CLOUD_NAME =
    process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME || 'resilia-staging'
  const CLOUDINARY_UPLOAD_PRESET =
    process.env.NEXT_PUBLIC_CLOUDINARY_UPLOAD_PRESET || 'default_unsigned'

  interface CloudinaryUploadResponse {
    secure_url: string
    public_id: string
    format: string
    width: number
    height: number
    original_filename: string
    resource_type: string
  }

  const handleUploadSuccess = async (response: CloudinaryUploadResponse) => {
    createAttachment({
      variables: {
        goalId,
        filename: response.original_filename,
        format: response.format,
        secureUrl: response.secure_url,
        publicId: response.public_id,
        resourceType: response.resource_type,
      },
    })
  }

  const handleUploadError = (error: Error) => {
    // Log error to monitoring service or handle silently
    // Error is already displayed to the user by the CloudinaryCustomUploads component
  }

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    attachment: Attachment
  ) => {
    setMenuAnchorEl(event.currentTarget)
    setSelectedAttachment(attachment)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
  }

  const handleEditClick = () => {
    if (selectedAttachment) {
      setActiveUpdate(selectedAttachment)
      setIsUpdateOpen(true)
    }
    handleMenuClose()
  }

  const handleDeleteClick = () => {
    if (selectedAttachment) {
      setActiveUpdate(selectedAttachment)
      setIsConfirmingDelete(true)
    }
    handleMenuClose()
  }

  const handleDeleteUpdate = (attachmentUpdateId: number) => {
    deleteAttachment({
      variables: { id: attachmentUpdateId },
    })
    setIsConfirmingDelete(false)
    setActiveUpdate(undefined)
  }

  const getFileIcon = (
    resourceType: string | null | undefined,
    format: string | null | undefined
  ) => {
    if (resourceType === 'image') {
      return <PhotoIcon color='primary' />
    }
    if (format?.toLowerCase() === 'pdf' || resourceType === 'pdf') {
      return <PictureAsPdfIcon color='primary' />
    }
    return <DescriptionIcon color='primary' />
  }

  const getUserInitials = (
    firstName: string | null | undefined,
    lastName: string | null | undefined
  ) => {
    const firstInitial = firstName?.charAt(0) || ''
    const lastInitial = lastName?.charAt(0) || ''
    return `${firstInitial}${lastInitial}`.toUpperCase()
  }

  const sortedAttachments = [...goal.attachments].sort(
    (a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()
  )

  const renderEmptyState = () => (
    <EmptyStateContainer>
      <IconContainer>
        <AttachFileOutlinedIcon sx={{ fontSize: 36, color: '#3361CC' }} />
      </IconContainer>
      <EmptyStateTitle>No Attachments</EmptyStateTitle>
      <EmptyStateText>
        Upload files, documents, and images to share with your team.
      </EmptyStateText>
    </EmptyStateContainer>
  )

  return (
    <Container>
      <HeaderAndButtonContainer>
        <ButtonContainer>
          <CloudinaryCustomUploads
            cloudName={CLOUDINARY_CLOUD_NAME}
            uploadPreset={CLOUDINARY_UPLOAD_PRESET}
            onSuccess={handleUploadSuccess}
            onError={handleUploadError}
          />
        </ButtonContainer>
      </HeaderAndButtonContainer>

      {goal.attachments.length > 0 ? (
        <>
          <HeaderRow>
            <HeaderCell>Attachment</HeaderCell>
            <HeaderCell>Added By</HeaderCell>
            <HeaderCell>Date</HeaderCell>
            <HeaderCell>Actions</HeaderCell>
          </HeaderRow>
          <ScrollableContent>
            {sortedAttachments.map((attachment) => (
              <AttachmentRow key={attachment.id}>
                <FileNameCell>
                  <AttachmentIcon>
                    {getFileIcon(attachment.resourceType, attachment.format)}
                  </AttachmentIcon>
                  <FileName>
                    <a
                      href={attachment.secureUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      {attachment.filename}
                      {attachment.format && ` (${attachment.format})`}
                    </a>
                  </FileName>
                </FileNameCell>
                <UserCell>
                  <UserAvatar>
                    {getUserInitials(
                      attachment.owner?.firstName,
                      attachment.owner?.lastName
                    )}
                  </UserAvatar>
                  <UserInfo>
                    {attachment.owner?.firstName} {attachment.owner?.lastName}
                  </UserInfo>
                </UserCell>
                <DateCell>
                  {dayjs(attachment.createdAt).format('MM/DD/YYYY')}
                </DateCell>
                <ActionsCell>
                  <ActionsButton
                    size='small'
                    onClick={(e) => handleMenuOpen(e, attachment)}
                  >
                    <MoreHorizIcon />
                  </ActionsButton>
                </ActionsCell>
              </AttachmentRow>
            ))}
          </ScrollableContent>
        </>
      ) : (
        renderEmptyState()
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            width: '180px',
          },
        }}
      >
        <MenuItem onClick={handleEditClick} sx={{ gap: '12px', py: '10px' }}>
          <EditIcon fontSize='small' />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ gap: '12px', py: '10px' }}>
          <DeleteIcon fontSize='small' />
          Delete
        </MenuItem>
      </Menu>

      <div ref={anchorRef}>
        <AttachmentUpdateDialog
          attachment={activeUpdate}
          open={isUpdateOpen}
          onUpdateComplete={() => setActiveUpdate(undefined)}
          onClose={() => {
            setIsUpdateOpen(false)
          }}
        />
      </div>

      {isConfirmingDelete && activeUpdate && (
        <ConfirmActionDialogSong
          title='Delete Attachment'
          open={isConfirmingDelete}
          submitButtonText='Delete'
          onCancelHandler={() => setIsConfirmingDelete(false)}
          onSubmitHandler={() => handleDeleteUpdate(activeUpdate.id)}
        >
          <TitleWithTooltipSong title='Are you sure you want to delete this attachment?' />
        </ConfirmActionDialogSong>
      )}
    </Container>
  )
}
